import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { observable, runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useMemo, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading5 } from "@/components/Heading/Heading.tsx"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import {
  Input_Bordered,
  Input_WithLockIcon,
  LockIconInside,
  ToggleSecret,
  ToggleSecret_Offset
} from "@/components/PromptInput/SecretField.ts"
import { CloseLight, Icon16, Icon20, OpenInNewTabRegular } from "@/icons/Icons.ts"
import { SECRET_VALUE_PLACEHOLDER } from "@/lib/displayUtils.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"

import type { ProjectMirroringModel } from "@/components/ProjectSettingsDialog/ProjectMirroringModel.ts"
import type { RepositoryAuthType } from "@/lib/client/generated/TypeDefinitions.ts"
import type { IObservableValue } from "mobx"
import type { ChangeEvent, DragEvent } from "react"


export interface RepositoryMirrorErrorProps {
  hasAuthError: boolean
  authOptions: RepositoryAuthType[]
  getTokenUrl: string | null
  saveToken: (token: string) => void
  saveSsh: (key: string, password: string | null) => void
  model: ProjectMirroringModel
  className?: string
  onClose: () => void
}

export const RepositoryCredUpdate = observer((props: RepositoryMirrorErrorProps) => {
  const { hasAuthError, authOptions, getTokenUrl, saveToken, saveSsh, className, model } = props

  const value = useMemo(() => observable.box(""), [])
  const passwordValue = useMemo(() => observable.box(""), [])
  const descriptionToken = "Couldn't fetch the latest version. New ideas will use an older version until you regenerate the access token."
  const descriptionTokenAskAdmin = "Couldn't fetch the latest version. New ideas will use an older version. Ask the administrator to regenerate the access token."
  const descriptionKey = "Couldn't fetch the latest version. New ideas will use an older version until you provide a valid SSH key."
  const descriptionKeyAskAdmin = "Couldn't fetch the latest version. New ideas will use an older version. Ask the administrator to provide a valid SSH key."

  const titleToken = "Update access token"
  const titleKey = "Update SSH key"

  const isEmpty = value.get().length === 0

  const isDragging = model.isDragging.get()

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!authOptions.includes("SSH")) return
    if (!e.dataTransfer.types.includes("Files")) return
    e.preventDefault()
    runInAction(() => model.isDragging.set(true))
  }

  const onDragLeave = () => {
    runInAction(() => model.isDragging.set(false))
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!authOptions.includes("SSH")) return
    e.preventDefault()
    runInAction(() => model.isDragging.set(false))

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      void model.onSshKeyUploaded(file, (text) => value.set(text))
    }
  }

  return (
    <Container
      $error={hasAuthError}
      gap="16px"
      className={cx(className, isDragging ? PromptInputStyles.Highlighted : null)}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {!hasAuthError && props.onClose && (
        <CloseIconButton kind="icon" onClick={props.onClose}>
          <CloseLight className={Icon16} />
        </CloseIconButton>
      )}
      <Horizontal gap="16px">
        <Vertical gap="4px">
          <Horizontal alignItems="center" gap="8px">
            {hasAuthError ? (
              <ErrorTitle>Failed to fetch repository</ErrorTitle>
            ) : (
              <Heading5>{(authOptions.includes("Token") ? titleToken : titleKey)}</Heading5>
            )}
          </Horizontal>

          {hasAuthError ? (
            <>
              {model.canEditProject.get() && (
                <Description>{(authOptions.includes("Token") ? descriptionToken : descriptionKey)}</Description>
              )}

              {!model.canEditProject.get() && (
                <Description>{(authOptions.includes("Token") ? descriptionTokenAskAdmin : descriptionKeyAskAdmin)}</Description>
              )}
            </>
          ) : (
            <>
              {model.canEditProject.get() && authOptions.includes("Token") && getTokenUrl && (
                <Description>
                  <ActionLink
                    href="#"
                    onClick={(e) => { e.preventDefault(); openLinkInNewWindow(getTokenUrl) }}
                  >
                    <OpenInNewTabRegular className={Icon20} />
                    Regenerate a token
                  </ActionLink>
                  &nbsp;and paste it here
                </Description>
              )}
            </>
          )}
        </Vertical>

        {hasAuthError && model.canEditProject.get() && getTokenUrl && authOptions.includes("Token") && (
          <Horizontal alignItems="center">
            <Button kind="secondary" size="large" onClick={() => openLinkInNewWindow(getTokenUrl)}>
              <Horizontal alignItems="center" gap="4px">
                <OpenInNewTabRegular className={Icon20} />
                Regenerate token
              </Horizontal>
            </Button>
          </Horizontal>
        )}
      </Horizontal>

      {model.canEditProject.get() && authOptions.includes("Token") && (
        <Horizontal gap="16px">
          <SecretInput
            value={value}
            onChange={(event) => runInAction(() => {
              value.set(event.target.value)
            })}
            placeholder="Enter access token"
          />

          <Button
            size="large"
            kind="primary"
            disabled={isEmpty || model.isSaving.get()}
            onClick={() => saveToken(value.get())}
          >
            Save
          </Button>
        </Horizontal>
      )}

      {model.canEditProject.get() && authOptions.includes("SSH") && (
        <Horizontal gap="16px">
          <SshInputColumn gap="8px">
            <SecretInput
              value={value}
              onChange={(event) => runInAction(() => {
                value.set(event.target.value)
              })}
              placeholder="Enter private SSH key or drag'n'drop a file here"
            />

            <SecretInput
              value={passwordValue}
              onChange={(event) => runInAction(() => {
                passwordValue.set(event.target.value)
              })}
              placeholder="Passphrase (optional)"
            />
          </SshInputColumn>

          <SaveKeyButton
            size="large"
            kind="primary"
            disabled={isEmpty || model.isSaving.get()}
            onClick={() => saveSsh(value.get(), passwordValue.get())}
          >
            Save
          </SaveKeyButton>
        </Horizontal>
      )}
    </Container>
  )
})

const SecretInput = observer((props: {
  value: IObservableValue<string>
  onChange: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  placeholder: string
}) => {

  const [showSecret, setShowSecret] = useState(false)

  return (
    <SecretInputContainer>
      <input
        className={cx(
          PromptInputStyles.Input,
          Input_Bordered,
          Input_WithLockIcon,
          Input_WithSecretToggle
        )}
        value={props.value.get()}
        placeholder={props.placeholder}
        type={showSecret ? "text" : "password"}
        onChange={props.onChange}
      />

      <LockIconInside />

      <StyledToggleSecret
        size="small"
        kind="alternative"
        className={ToggleSecret_Offset}
        disabled={props.value.get().length === 0 || props.value.get() === SECRET_VALUE_PLACEHOLDER}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setShowSecret(!showSecret)
        }}
      >
        {showSecret ? "Hide" : "Show"}
      </StyledToggleSecret>
    </SecretInputContainer>
  )
})

const ErrorTitle = styled(Heading5)`
  color: var(--error-color);
`

const Container = styled(Vertical)<{ $error: boolean }>`
  position: relative;
  padding: 16px;
  border-radius: var(--border-radius-12);
  background: ${p => p.$error ? "var(--error-color-10)" : "var(--text-color-2)"};
`

const Description = styled(Horizontal)`
  align-items: center;
  color: var(--secondary-text-color);
  font-size: var(--small-font-size);
  line-height: 18px;
`

const ActionLink = styled.a`
  color: var(--main-color);
  cursor: pointer;
  text-decoration: none;

  display: inline-flex;
  align-items: center;
`

const Input_WithSecretToggle = css`
  background: var(--background-color);
  border-radius: var(--border-radius-12);

  /* Make room for the inline ToggleSecret button (text width + spacing + right inset) */
  padding-right: 72px;
`

const StyledToggleSecret = styled(ToggleSecret)`
  background: transparent;

  &:hover:not(:disabled) {
    color: var(--main-color);
    background: transparent;
  }
`

const SecretInputContainer = styled(Horizontal)`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
`

const SshInputColumn = styled(Vertical)`
  flex: 1 1 auto;
  min-width: 0;
`

const SaveKeyButton = styled(Button)`
  align-self: flex-end;
`

const CloseIconButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 16px;
`